import { useState, useEffect, useMemo } from 'react'
import { X, Search, Loader2, Store } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  SheetClose,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Pagination } from '@/components/common/Pagination'
import { ConfirmationDialog } from '@/components/common/ConfirmationDialog'
import { StoreGroupSelector } from './StoreGroupSelector'
import {
  useStoreModelSearch,
  useStoreModelDetail,
  useAssignStoreModel,
  useUnassignStoreModel,
  useUpdateStoreGroups,
  useModelGroupsInfinite,
} from '@/hooks/useStoreModels'
import { getModelGroupMembers } from '@/api/storeModels'
import { queryKeys } from '@/lib/queryKeys'
import { toast } from '@/stores/useToastStore'
import type { StoreModelRead, StoreGroupMemberListResponse } from '@/types'

type DrawerStep = 'search' | 'groups'

interface AssignStoreModelDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  planId: string
  versionId: string
  lineIds: string[]
  existingStoreModelId?: string | null
  startAtGroups?: boolean
  initialStoreGroupIds?: string[]
  onSuccess?: () => void
}

export function AssignStoreModelDrawer({
  open,
  onOpenChange,
  planId,
  versionId,
  lineIds,
  existingStoreModelId,
  startAtGroups = false,
  initialStoreGroupIds,
  onSuccess,
}: AssignStoreModelDrawerProps) {
  const [step, setStep] = useState<DrawerStep>('search')
  const [searchQuery, setSearchQuery] = useState('')
  const [searchActive, setSearchActive] = useState(false)
  const [selectedModel, setSelectedModel] = useState<StoreModelRead | null>(
    null
  )
  const [storesPage, setStoresPage] = useState(1)
  const [storesPageSize, setStoresPageSize] = useState(10)
  const [isPreAssigned, setIsPreAssigned] = useState(false)
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false)
  const [wasUnassigned, setWasUnassigned] = useState(false)
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([])
  const [viewingGroupId, setViewingGroupId] = useState<string>('')
  const [groupsInitialized, setGroupsInitialized] = useState(false)

  const { data: existingModelDetail, isLoading: isLoadingExistingModel } =
    useStoreModelDetail(
      open && existingStoreModelId ? existingStoreModelId : undefined
    )

  const activeModelId = selectedModel?.id ?? existingStoreModelId
  const {
    data: allGroupsPages,
    fetchNextPage,
    hasNextPage,
  } = useModelGroupsInfinite(
    open && activeModelId ? activeModelId : undefined,
    ''
  )

  // Seed selectedModel from fetched existing model data
  useEffect(() => {
    if (
      open &&
      existingStoreModelId &&
      existingModelDetail &&
      !selectedModel &&
      !wasUnassigned
    ) {
      setSelectedModel(existingModelDetail as StoreModelRead)
      setIsPreAssigned(true)
      setStep('groups')
    }
  }, [
    open,
    existingStoreModelId,
    existingModelDetail,
    selectedModel,
    wasUnassigned,
    startAtGroups,
  ])

  useEffect(() => {
    if (!open || groupsInitialized || !allGroupsPages?.pages.length) return

    if (initialStoreGroupIds) {
      setSelectedGroupIds(initialStoreGroupIds)
      setGroupsInitialized(true)
      return
    }

    if (hasNextPage) {
      fetchNextPage()
      return
    }

    const allIds = allGroupsPages.pages.flatMap(p => p.data.map(g => g.id))
    setSelectedGroupIds(allIds)
    setGroupsInitialized(true)
  }, [
    open,
    allGroupsPages,
    initialStoreGroupIds,
    groupsInitialized,
    hasNextPage,
    fetchNextPage,
  ])

  useEffect(() => {
    if (!open) {
      setStep('search')
      setSearchQuery('')
      setSearchActive(false)
      setSelectedModel(null)
      setStoresPage(1)
      setStoresPageSize(10)
      setIsPreAssigned(false)
      setConfirmRemoveOpen(false)
      setWasUnassigned(false)
      setSelectedGroupIds([])
      setGroupsInitialized(false)
      setViewingGroupId('')
    }
  }, [open])

  const { data: modelsData, isLoading: isSearching } = useStoreModelSearch({
    query: searchQuery,
    enabled: open && searchActive,
  })

  const { data: groupMembersData, isLoading: isLoadingGroupMembers } =
    useQuery<StoreGroupMemberListResponse>({
      queryKey: queryKeys.storeModels.groupMembers(
        selectedModel?.id ?? '',
        viewingGroupId,
        { page: storesPage, pageSize: storesPageSize }
      ),
      queryFn: () =>
        getModelGroupMembers(selectedModel!.id, viewingGroupId, {
          skip: (storesPage - 1) * storesPageSize,
          limit: storesPageSize,
        }),
      enabled: !!selectedModel?.id && !!viewingGroupId,
      staleTime: 60_000,
    })

  const assignMutation = useAssignStoreModel(planId, versionId)
  const unassignMutation = useUnassignStoreModel(planId, versionId)
  const updateGroupsMutation = useUpdateStoreGroups(planId, versionId)

  const handleSelectModel = (model: StoreModelRead) => {
    setSelectedModel(model)
    setIsPreAssigned(false)
    setStep('groups')
    setStoresPage(1)
    setGroupsInitialized(false)
    setSelectedGroupIds([])
  }

  const handleBack = () => {
    setSelectedModel(null)
    setIsPreAssigned(false)
    setStep('search')
    setStoresPage(1)
  }

  const handleAssign = () => {
    if (!selectedModel) return

    assignMutation.mutate(
      { lineIds, storeModelId: selectedModel.id },
      {
        onSuccess: assignResponse => {
          if (assignResponse.failed > 0) {
            toast.error(
              `Assigned store model to ${assignResponse.succeeded} item(s). ${assignResponse.failed} failed.`
            )
          }
          if (selectedGroupIds.length > 0) {
            updateGroupsMutation.mutate(
              { lineIds, storeGroupIds: selectedGroupIds },
              {
                onSuccess: groupsResponse => {
                  if (groupsResponse.failed > 0) {
                    toast.error(
                      `Updated store groups for ${groupsResponse.succeeded} item(s). ${groupsResponse.failed} failed.`
                    )
                  } else {
                    toast.success(
                      'Store Model assigned and groups updated successfully'
                    )
                  }
                  onOpenChange(false)
                  onSuccess?.()
                },
                onError: (error: Error) => {
                  toast.error(
                    `Store model assigned but failed to update groups: ${error.message}`
                  )
                  setIsPreAssigned(true)
                },
              }
            )
          } else {
            toast.success('Store Model successfully assigned')
            onOpenChange(false)
            onSuccess?.()
          }
        },
        onError: (error: Error) => {
          toast.error(`Failed to assign store model: ${error.message}`)
        },
      }
    )
  }

  const handleUnassign = () => {
    unassignMutation.mutate(
      { lineIds },
      {
        onSuccess: response => {
          if (response.failed > 0) {
            toast.error(
              `Removed store model from ${response.succeeded} item(s). ${response.failed} failed.`
            )
          } else {
            toast.success('Store Model successfully removed')
          }
          setConfirmRemoveOpen(false)
          setSelectedModel(null)
          setIsPreAssigned(false)
          setWasUnassigned(true)
          setSearchActive(false)
          setStep('search')
          onSuccess?.()
        },
        onError: (error: Error) => {
          toast.error(`Failed to remove store model: ${error.message}`)
        },
      }
    )
  }

  const handleConfirmGroups = () => {
    updateGroupsMutation.mutate(
      { lineIds, storeGroupIds: selectedGroupIds },
      {
        onSuccess: response => {
          if (response.failed > 0) {
            toast.error(
              `Updated store groups for ${response.succeeded} item(s). ${response.failed} failed.`
            )
          } else {
            toast.success('Store groups successfully updated')
          }
          onOpenChange(false)
          onSuccess?.()
        },
        onError: (error: Error) => {
          toast.error(`Failed to update store groups: ${error.message}`)
        },
      }
    )
  }

  const storesTotalPages = groupMembersData
    ? Math.ceil(groupMembersData.pagination.total / storesPageSize)
    : 0

  const selectedGroups = useMemo(
    () =>
      (allGroupsPages?.pages.flatMap(p => p.data) ?? []).filter(g =>
        selectedGroupIds.includes(g.id)
      ),
    [allGroupsPages, selectedGroupIds]
  )

  const showLoadingExistingModel =
    !!existingStoreModelId && isLoadingExistingModel && !selectedModel

  const headerTitle =
    step === 'groups'
      ? isPreAssigned
        ? 'Manage Store Groups'
        : 'Assign Store Model'
      : 'Assign Store Model'

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        showCloseButton={false}
        className="flex w-[600px] flex-col sm:max-w-[600px]"
      >
        {/* Header */}
        <SheetHeader className="border-b px-6 py-4">
          <div className="flex items-center justify-between">
            <SheetTitle className="text-lg font-semibold">
              {headerTitle}
            </SheetTitle>
            <SheetClose asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 cursor-pointer"
              >
                <X className="h-4 w-4" />
                <span className="sr-only">Close</span>
              </Button>
            </SheetClose>
          </div>
        </SheetHeader>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {showLoadingExistingModel ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : step === 'search' ? (
            // Step 1: Search & Select (command-palette style)
            <div className="rounded-md border">
              <div className="relative border-b">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  onFocus={() => setSearchActive(true)}
                  className="border-0 shadow-none pl-9 focus-visible:ring-0"
                />
              </div>

              {searchActive && (
                <div className="p-2">
                  <p className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                    Suggestions
                  </p>

                  {isSearching && (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    </div>
                  )}

                  {!isSearching && modelsData?.data.length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-6">
                      No store models found.
                    </p>
                  )}

                  {!isSearching &&
                    modelsData?.data.map(model => (
                      <button
                        key={model.id}
                        onClick={() => handleSelectModel(model)}
                        className="flex w-full cursor-pointer items-center gap-3 rounded-sm px-2 py-1.5 text-left hover:bg-accent transition-colors"
                      >
                        <Store className="h-5 w-5 shrink-0 text-muted-foreground" />
                        <span className="flex-1 text-sm font-medium text-muted-foreground">
                          {model.name}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {model.store_count}{' '}
                          {model.store_count === 1 ? 'store' : 'stores'}
                        </span>
                      </button>
                    ))}
                </div>
              )}
            </div>
          ) : (
            // Step 2: Store Group Selection (skips old preview step)
            selectedModel && (
              <div className="space-y-4">
                {/* Model info header */}
                <div className="flex items-center gap-3 justify-between">
                  <div className="flex items-center gap-3">
                    <div className="bg-primary rounded-[10px] size-10 flex items-center justify-center shrink-0">
                      <Store className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold">
                        {selectedModel.name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {selectedModel.store_count}{' '}
                        {selectedModel.store_count === 1 ? 'store' : 'stores'}
                        {' · '}Select which store groups to assign
                      </p>
                    </div>
                  </div>
                  {!isPreAssigned && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="cursor-pointer"
                      onClick={handleBack}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>

                <StoreGroupSelector
                  modelId={selectedModel.id}
                  initialSelectedGroupIds={initialStoreGroupIds}
                  selectedGroupIds={selectedGroupIds}
                  onSelectionChange={setSelectedGroupIds}
                />

                {/* Expandable stores by group */}
                <details className="rounded-md border">
                  <summary className="flex items-center gap-2 px-3 py-2.5 text-sm font-medium cursor-pointer hover:bg-muted/50 transition-colors">
                    <Store className="h-4 w-4 text-muted-foreground" />
                    View Stores by Group
                  </summary>
                  <div className="border-t">
                    {/* Group filter tabs */}
                    {selectedGroupIds.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 p-2 border-b bg-muted/30">
                        {selectedGroups.map(g => (
                          <button
                            key={g.id}
                            onClick={() => {
                              setViewingGroupId(g.id)
                              setStoresPage(1)
                            }}
                            className={`px-2.5 py-1 text-xs rounded-md cursor-pointer transition-colors ${
                              viewingGroupId === g.id
                                ? 'bg-primary text-primary-foreground'
                                : 'bg-background border border-border hover:bg-muted'
                            }`}
                          >
                            {g.name} ({g.member_count})
                          </button>
                        ))}
                      </div>
                    )}

                    {!viewingGroupId && (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        Select a group above to view its stores.
                      </p>
                    )}
                    {viewingGroupId && isLoadingGroupMembers && (
                      <div className="flex items-center justify-center py-6">
                        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                      </div>
                    )}
                    {viewingGroupId &&
                      !isLoadingGroupMembers &&
                      groupMembersData && (
                        <div>
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b bg-muted/50">
                                <th className="text-left px-3 py-2 font-semibold text-muted-foreground">
                                  Store #
                                </th>
                                <th className="text-left px-3 py-2 font-semibold text-muted-foreground">
                                  Name
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {groupMembersData.data.length === 0 ? (
                                <tr>
                                  <td
                                    colSpan={2}
                                    className="text-center py-4 text-muted-foreground"
                                  >
                                    No stores in this group.
                                  </td>
                                </tr>
                              ) : (
                                groupMembersData.data.map(member => (
                                  <tr
                                    key={member.id}
                                    className="border-b last:border-0"
                                  >
                                    <td className="px-3 py-1.5 font-medium">
                                      {member.store_number}
                                    </td>
                                    <td className="px-3 py-1.5 text-muted-foreground">
                                      {member.store_name}
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                          {groupMembersData.pagination.total > 0 && (
                            <Pagination
                              page={storesPage}
                              pageSize={storesPageSize}
                              totalPages={storesTotalPages}
                              onPageChange={setStoresPage}
                              onPageSizeChange={size => {
                                setStoresPageSize(size)
                                setStoresPage(1)
                              }}
                            />
                          )}
                        </div>
                      )}
                  </div>
                </details>
              </div>
            )
          )}
        </div>

        {/* Footer */}
        {step === 'groups' && selectedModel && (
          <SheetFooter className="border-t px-6 py-4">
            <div className="flex items-center justify-between w-full">
              <div>
                {isPreAssigned && (
                  <button
                    type="button"
                    className="text-sm text-destructive hover:text-destructive/80 underline cursor-pointer"
                    onClick={() => setConfirmRemoveOpen(true)}
                  >
                    Remove Store Model
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                {isPreAssigned ? (
                  <Button
                    onClick={handleConfirmGroups}
                    disabled={
                      selectedGroupIds.length === 0 ||
                      updateGroupsMutation.isPending
                    }
                  >
                    {updateGroupsMutation.isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        Confirming...
                      </>
                    ) : (
                      'Confirm'
                    )}
                  </Button>
                ) : (
                  <Button
                    onClick={handleAssign}
                    disabled={
                      selectedGroupIds.length === 0 ||
                      assignMutation.isPending ||
                      updateGroupsMutation.isPending
                    }
                  >
                    {assignMutation.isPending ||
                    updateGroupsMutation.isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        Assigning...
                      </>
                    ) : (
                      'Assign Store Model'
                    )}
                  </Button>
                )}
              </div>
            </div>
          </SheetFooter>
        )}
      </SheetContent>

      <ConfirmationDialog
        open={confirmRemoveOpen}
        onOpenChange={setConfirmRemoveOpen}
        title="Remove Store Model"
        description="Are you sure you want to remove the store model assignment? This action cannot be undone."
        variant="destructive"
        confirmText="Remove"
        cancelText="Cancel"
        isLoading={unassignMutation.isPending}
        onConfirm={handleUnassign}
      />
    </Sheet>
  )
}
